// src/hooks/useKatarutaSuggest.ts
//
// 「。」を検知して /api/kataruta/suggest を呼び、ワード候補を管理するフック。
//
// チップモード  : 「。」→ ピルが出る → クリックで採用、キー入力で dismiss
// ダイレクトモード: 「。」→ ワードを「。」直後に即自動挿入、チップなし

"use client";

import { useCallback, useRef, useState } from "react";
import { getCursorCoords } from "@/lib/getCursorCoords";
import { useEventLogger } from "@/lib/useEventLogger";

export type FreqMode = "always" | "sparse";
export type SuggestMode = "chip" | "direct";
export type CardTypeKey =
  | "standard"
  | "logical"
  | "storytelling"
  | "life"
  | "emotion";

export interface Suggestion {
  word: string;
  cardType: CardTypeKey;
}

interface UseSuggestOptions {
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  getText: () => string;
  setText: (v: string) => void;
  defaultEnabled?: boolean; // デフォルトON/OFF（省略時はfalse）
}

export function useKatarutaSuggest({
  textareaRef,
  getText,
  setText,
  defaultEnabled = false,
}: UseSuggestOptions) {
  const { log: logEvent } = useEventLogger();

  const [enabled, setEnabledState] = useState(defaultEnabled);
  const [suggestMode, setSuggestModeState] = useState<SuggestMode>("chip");
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const [cardType, setCardType] = useState<CardTypeKey>("standard");
  const [freqMode, setFreqModeState] = useState<FreqMode>("always");
  const [isPaidOrTrial, setIsPaidOrTrial] = useState(false);

  // 2026-07-13：保存しないと何も行動記録が残らない問題への対応（Kazuto指摘）。
  // トグル操作自体をイベントとして記録する（/api/events の ALLOWED_EVENTS に追加要）。
  const setEnabled = useCallback((v: boolean) => {
    logEvent("text_mode_toggle", { enabled: v });
    setEnabledState(v);
  }, [logEvent]);

  const setSuggestMode = useCallback((m: SuggestMode) => {
    logEvent("text_suggest_mode_change", { mode: m });
    setSuggestModeState(m);
  }, [logEvent]);

  const setFreqMode = useCallback((m: FreqMode) => {
    logEvent("text_suggest_freq_change", { freq: m });
    setFreqModeState(m);
  }, [logEvent]);

  const prevDotCountRef = useRef(0);
  const fetchingRef = useRef(false);
  // 初回フォーカストリガー用（保存後にリセット）
  const hasTriggeredInitiallyRef = useRef(false);
  // 「自然」モード用カウンター（確率ではなく周期で制御して振れ幅を抑える）
  const sparseCounterRef   = useRef(0);
  const sparseThresholdRef = useRef(3); // 初回は3回目で発火
  // ref で最新値を保持（async コールバック内での stale closure 回避）
  const cardTypeRef = useRef<CardTypeKey>(cardType);
  const freqModeRef = useRef<FreqMode>(freqMode);
  const suggestModeRef = useRef<SuggestMode>(suggestMode);
  const getTextRef = useRef(getText);
  const setTextRef = useRef(setText);
  cardTypeRef.current = cardType;
  freqModeRef.current = freqMode;
  suggestModeRef.current = suggestMode;
  getTextRef.current = getText;
  setTextRef.current = setText;

  const dismiss = useCallback(() => {
    setSuggestion(null);
    setCoords(null);
  }, []);

  // ── チップモード用：候補を取得して表示 ──────────────────────────
  const fetchSuggestion = useCallback(async (ct: CardTypeKey) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetch(`/api/kataruta/suggest?cardType=${ct}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.word) {
        setSuggestion({ word: data.word, cardType: data.cardType ?? ct });
        setIsPaidOrTrial(data.isPaidOrTrial ?? false);
        if (textareaRef.current) {
          setCoords(getCursorCoords(textareaRef.current));
        }
      }
    } catch {
      // サイレントに無視
    } finally {
      fetchingRef.current = false;
    }
  }, [textareaRef]);

  // ── ダイレクトモード用：取得して即挿入 ──────────────────────────
  const fetchAndInsert = useCallback(async (ct: CardTypeKey, insertPos: number) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await fetch(`/api/kataruta/suggest?cardType=${ct}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.word) {
        setIsPaidOrTrial(data.isPaidOrTrial ?? false);
        const current = getTextRef.current();
        const updated = current.slice(0, insertPos) + data.word + current.slice(insertPos);
        setTextRef.current(updated);
        logEvent("text_suggest_adopted", { word: data.word, cardType: data.cardType ?? ct, mode: "direct" });
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (!el) return;
          const newPos = insertPos + data.word.length;
          el.setSelectionRange(newPos, newPos);
          el.focus();
        });
      }
    } catch {
      // サイレントに無視
    } finally {
      fetchingRef.current = false;
    }
  }, [textareaRef, logEvent]);

  const onTextChange = useCallback(
    (newText: string) => {
      if (!enabled) return;
      const newDotCount = (newText.match(/。/g) ?? []).length;
      const added = newDotCount - prevDotCountRef.current;
      prevDotCountRef.current = newDotCount;

      if (added <= 0) return;
      if (freqModeRef.current === "sparse") {
        sparseCounterRef.current += added;
        if (sparseCounterRef.current < sparseThresholdRef.current) return;
        // 発火 → カウンターリセット・次の閾値を2か3にランダム設定
        sparseCounterRef.current = 0;
        sparseThresholdRef.current = Math.random() < 0.5 ? 3 : 4;
      }

      if (suggestModeRef.current === "direct") {
        // 「。」直後のカーソル位置を今すぐキャプチャしてから非同期挿入
        const insertPos = textareaRef.current?.selectionStart ?? newText.length;
        fetchAndInsert(cardTypeRef.current, insertPos);
      } else {
        setSuggestion(null);
        setCoords(null);
        fetchSuggestion(cardTypeRef.current);
      }
    },
    [enabled, textareaRef, fetchSuggestion, fetchAndInsert]
  );

  const onKeyDown = useCallback(() => {
    if (suggestion) dismiss();
  }, [suggestion, dismiss]);

  // 初回フォーカス時に1回だけワードを表示（チップ/ダイレクトそれぞれの挙動で）
  const onFocus = useCallback(() => {
    if (!enabled) return;
    if (hasTriggeredInitiallyRef.current) return;
    hasTriggeredInitiallyRef.current = true;
    setSuggestion(null);
    setCoords(null);
    if (suggestModeRef.current === "direct") {
      const insertPos = textareaRef.current?.selectionStart ?? 0;
      fetchAndInsert(cardTypeRef.current, insertPos);
    } else {
      fetchSuggestion(cardTypeRef.current);
    }
  }, [enabled, textareaRef, fetchSuggestion, fetchAndInsert]);

  // 保存後に呼ぶ：次の入力セッションで再び初回トリガーを発火させる
  const resetInitialTrigger = useCallback(() => {
    hasTriggeredInitiallyRef.current = false;
  }, []);

  // チップモード用：採用でカーソル位置に挿入
  const adopt = useCallback(() => {
    if (!suggestion) return;
    const el = textareaRef.current;
    const current = getText();
    const pos = el?.selectionStart ?? current.length;
    const updated = current.slice(0, pos) + suggestion.word + current.slice(pos);
    setText(updated);
    logEvent("text_suggest_adopted", { word: suggestion.word, cardType: suggestion.cardType, mode: "chip" });
    dismiss();
    requestAnimationFrame(() => {
      if (!el) return;
      const newPos = pos + suggestion.word.length;
      el.setSelectionRange(newPos, newPos);
      el.focus();
    });
  }, [suggestion, textareaRef, getText, setText, dismiss, logEvent]);

  return {
    enabled,
    setEnabled,
    suggestMode,
    setSuggestMode,
    suggestion,
    coords,
    cardType,
    setCardType,
    freqMode,
    setFreqMode,
    isPaidOrTrial,
    adopt,
    dismiss,
    onTextChange,
    onKeyDown,
    onFocus,
    resetInitialTrigger,
  };
}
